import { checkReportRange, type ReportResult, type UsageRow } from "@fts/core";
import { useMemo } from "react";
import { PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { durationLabel, operatorText, usageSheet } from "../../lib/report-sheets";
import type { Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { ExportBar, FilterBar, Loading, ReportMeta, filterSummary, parseFilters, rangeKey, useLoad, type ExportSpec } from "./shared";
import { loadWorkHours, scopeSiteIds, type WorkHoursInfo } from "./work-hours";

/** Kullanım raporu (ADR 0018): per vehicle and local day — ignition, moving, idle, distance, lifts, utilisation, operators. */
export function UsageReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const issue = checkReportRange(f.from, f.to);
  const tenantId = tenant?.id;
  const query = (id: string) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: f.operatorId, from: f.from, to: f.to, basis: f.basis });
  const state = useLoad<ReportResult<UsageRow>>(
    tenantId && !issue ? () => provider.reportUsage(query(tenantId)) : null,
    [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.operatorId, f.from, f.to, f.basis],
  );
  const scope = scopeSiteIds(data, f.siteId, f.vehicleIds);
  const hours = useLoad<WorkHoursInfo>(() => loadWorkHours(provider, t, data, scope), [provider, scope.join(",")]);
  const workHours = hours.data ?? null;
  const rows = state.data?.rows ?? [];
  // Operator names are personal data: shown (and exported) only for roles that can see operators (operators RLS).
  const hasOps = (rs: UsageRow[]) => rs.some((r) => r.operatorNames.length > 0 || r.operatorsHidden > 0 || r.operatorsUnknown > 0);
  const withOperators = hasOps(rows);
  const totals = useMemo(() => {
    const ign = rows.reduce((s, r) => s + r.ignitionS, 0);
    const basis = rows.reduce((s, r) => s + r.basisS, 0);
    const bi = rows.reduce((s, r) => s + r.basisIgnitionS, 0);
    return { ign, mov: rows.reduce((s, r) => s + r.movingS, 0), km: rows.reduce((s, r) => s + r.distanceM, 0) / 1000, util: basis > 0 ? (100 * bi) / basis : null, vehicles: new Set(rows.map((r) => r.vehicleId)).size };
  }, [rows]);
  const num = (v: number, d = 1) => v.toLocaleString(locale, { minimumFractionDigits: d, maximumFractionDigits: d });
  const spec: ExportSpec | null = state.status === "ready" && state.data && tenantId
    ? {
        report: "usage",
        reportName: t("reports.usage.title"),
        slug: t("reports.usage.slug"),
        part: `${f.from}_${f.to}`,
        tz: state.data.tz,
        filters: filterSummary(t, data, f, { basis: true, workHours }),
        rows: rows.length,
        load: async (format) => {
          const r = await provider.exportUsage(query(tenantId), format);
          return { sheets: [usageSheet(t, r.rows, hasOps(r.rows))], tz: r.tz };
        },
      }
    : null;
  const rk = rangeKey(issue);

  return (
    <>
      <PageHeader title={t("reports.usage.title")} subtitle={t("reports.usage.subtitle")} eyebrow={t("nav.group.reports")} />
      <FilterBar data={data} path="/reports/usage" filters={f} basis workHours={workHours} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      <ReportMeta tz={state.data?.tz ?? null} sample={provider.mode === "demo"} />
      {state.status === "ready" && rows.length > 0 && (
        <div className="report-totals" aria-label={t("reports.totals")}>
          <div>
            <span>{t("reports.usage.totalIgnition")}</span>
            <strong>{durationLabel(t, totals.ign)}</strong>
          </div>
          <div>
            <span>{t("reports.usage.totalMoving")}</span>
            <strong>{durationLabel(t, totals.mov)}</strong>
          </div>
          <div>
            <span>{t("reports.usage.totalDistance")}</span>
            <strong>{t("reports.km", { km: num(totals.km) })}</strong>
          </div>
          <div>
            <span>{t("reports.usage.avgUtilisation")}</span>
            <strong>{totals.util === null ? t("common.empty") : `%${num(totals.util)}`}</strong>
          </div>
          <div>
            <span>{t("reports.usage.vehiclesUsed")}</span>
            <strong>{totals.vehicles}</strong>
          </div>
        </div>
      )}
      <ExportBar spec={spec} disabled={rows.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      <Panel flush meta={state.status === "ready" ? <span>{t("reports.rows", { count: rows.length })}</span> : undefined}>
        {state.status === "loading" || state.status === "idle" ? (
          rk ? <p className="table-empty">{t(rk)}</p> : <Loading />
        ) : rows.length === 0 ? (
          <p className="table-empty">{t("reports.noData")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table report-table">
              <thead>
                <tr>
                  <th scope="col">{t("reports.col.vehicle")}</th>
                  <th scope="col">{t("reports.col.day")}</th>
                  <th scope="col" className="num">{t("reports.col.ignition")}</th>
                  <th scope="col" className="num">{t("reports.col.moving")}</th>
                  <th scope="col" className="num col-hide-sm">{t("reports.col.idle")}</th>
                  <th scope="col" className="num">{t("reports.col.distanceKm")}</th>
                  <th scope="col" className="num col-hide-md">{t("reports.col.lifts")}</th>
                  <th scope="col" className="num">{t("reports.col.utilisation")}</th>
                  {withOperators && <th scope="col" className="col-hide-md">{t("reports.col.operators")}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.vehicleId}|${r.day}`}>
                    <td>
                      <a className="subtle-link mono" href={`#/vehicles/${encodeURIComponent(r.vehicleId)}`}>
                        {r.fleetNo}
                      </a>
                    </td>
                    <td className="nowrap">{new Date(`${r.day}T12:00:00Z`).toLocaleDateString(locale, { day: "2-digit", month: "short", weekday: "short", timeZone: "UTC" })}</td>
                    <td className="num">{durationLabel(t, r.ignitionS)}</td>
                    <td className="num">{durationLabel(t, r.movingS)}</td>
                    <td className="num col-hide-sm">{durationLabel(t, r.idleS)}</td>
                    <td className="num">{num(r.distanceM / 1000, 2)}</td>
                    <td className="num col-hide-md">
                      {r.liftCount === null ? (
                        <span className="muted" title={t("reports.usage.noLiftModule")}>{t("common.empty")}</span>
                      ) : (
                        <>
                          {r.liftCount}
                          {r.liftedKg !== null && <span className="muted"> · {t("reports.kg", { kg: r.liftedKg.toLocaleString(locale) })}{r.liftEstimated ? ` ${t("reports.estimated")}` : ""}</span>}
                        </>
                      )}
                    </td>
                    <td className="num" title={t(`reports.basis.${r.basis}Hint`)}>
                      {r.utilisation === null ? t("common.empty") : `%${num(r.utilisation)}`}
                      <span className="basis-tag">{t(`reports.basis.${r.basis}Short`)}</span>
                    </td>
                    {withOperators && <td className="col-hide-md">{operatorText(t, r.operatorNames, r.operatorsHidden, r.operatorsUnknown) || t("common.empty")}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("reports.usage.rules")}</p>
    </>
  );
}
